import { MASTERY_MAX_POINTS } from "@riseafrica/scoring";
import { useEffect, useState } from "react";
import { CONFIG } from "../config";

/**
 * Vérification publique d'une attestation (ticket T13).
 *
 * Le lecteur de cette page est un employeur ou un client, pas le titulaire.
 * Deux conséquences : un numéro inconnu obtient une phrase claire et non une
 * erreur technique, et la page dit **ce que l'attestation ne prouve pas** —
 * sans quoi elle serait lue comme une certification de compétence.
 */

interface Verification {
  trouvee: boolean;
  message?: string;
  serial?: string;
  niveau?: number;
  niveauLibelle?: string;
  points?: number;
  nom?: string | null;
  metier?: string | null;
  emiseLe?: string | null;
  portee?: string;
}

/**
 * L'entrée de la vérification — la porte qui manquait.
 *
 * La page de vérification existait, mais il fallait déjà connaître l'adresse
 * complète pour l'atteindre : un recruteur à qui l'on montre une attestation
 * n'avait aucun endroit où **saisir** le numéro. Or c'est exactement le geste
 * qui rend la crédibilité praticable — une attestation invérifiable en
 * pratique est invérifiable tout court.
 */
export function AttestationEntree() {
  const [numero, setNumero] = useState("");
  const [invalide, setInvalide] = useState(false);

  useEffect(() => {
    document.title = "Vérifier une attestation | STRATERA";
  }, []);

  const verifier = () => {
    // Tolérant à la saisie réelle : espaces, minuscules, « RA » sans tiret.
    // Un numéro se recopie depuis un papier ou un écran, rarement sans accroc.
    const propre = numero.toUpperCase().replace(/\s+/g, "").replace(/^RA-?/, "");
    if (!/^[A-Z0-9-]{4,32}$/.test(propre)) {
      setInvalide(true);
      return;
    }
    window.location.assign(`/attestation/RA-${propre}`);
  };

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">Vérification d'attestation</div>
        <h1 style={{ marginTop: "6px" }}>Vérifier une attestation STRATERA</h1>
        <p className="sub">
          Chaque attestation porte un numéro au format <b>RA-2026-XXXX-XXXX</b>. Saisissez-le : cette page
          confirme s'il a bien été émis par nous, à quelle date, et pour quel niveau — ou dit
          clairement qu'il ne l'a pas été.
        </p>

        <div className="field">
          <label htmlFor="att-num">Numéro de l'attestation</label>
          <input
            id="att-num"
            placeholder="RA-2026-XXXX-XXXX"
            autoComplete="off"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") verifier();
            }}
            aria-invalid={invalide}
            aria-describedby={invalide ? "att-num-erreur" : undefined}
          />
          {invalide && (
            <p id="att-num-erreur" className="fine" style={{ color: "var(--high)" }} role="alert">
              Ce numéro ne ressemble pas à un numéro d'attestation. Il figure en bas du document,
              sous la forme RA- suivi de lettres et de chiffres.
            </p>
          )}
        </div>
        <button type="button" className="btn btn-brass" onClick={verifier}>
          Vérifier ce numéro
        </button>

        <p className="fine" style={{ marginTop: "18px" }}>
          Ce que la vérification prouve : que l'attestation a été émise par STRATERA, à cette date,
          pour ce niveau. Ce qu'elle ne prouve pas : une compétence professionnelle — la mesure
          repose sur les déclarations de la personne, et la page de résultat le dit.
        </p>
      </div>
    </section>
  );
}

export function Attestation({ serial }: { serial: string }) {
  const [etat, setEtat] = useState<Verification | null>(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    document.title = `Vérification d'attestation ${serial} | STRATERA`;
    let vivant = true;
    fetch(`${CONFIG.attestationEndpoint}?serial=${encodeURIComponent(serial)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: Verification) => vivant && setEtat(corps))
      .catch(() => vivant && setErreur(true));
    return () => {
      vivant = false;
    };
  }, [serial]);

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">Vérification d'attestation</div>
        <h1 style={{ marginTop: "6px" }}>{serial}</h1>

        {/* Annonce du résultat de la vérification, pour les lecteurs d'écran. */}
        <div className="sr" role="status" aria-live="polite">
          {etat === null ? "Vérification en cours" : etat.trouvee ? "Attestation valide" : "Attestation introuvable"}
        </div>

        {erreur && (
          <div className="err">
            La vérification n'a pas abouti. Réessayez dans un instant — l'absence de réponse ne
            signifie pas que l'attestation est invalide.
          </div>
        )}

        {etat === null && !erreur && <p className="lede">Vérification en cours…</p>}

        {etat?.trouvee === false && (
          <div className="block">
            <h2>Attestation introuvable</h2>
            <p>{etat.message}</p>
            <p className="fine">
              Vérifiez la saisie du numéro. Une attestation STRATERA se présente sous la forme
              <code> RA-2026-XXXX-XXXX</code>. Si le numéro est correct et reste introuvable, aucune
              attestation n'a été émise sous ce numéro.
            </p>
          </div>
        )}

        {etat?.trouvee === true && (
          <div className="block">
            <div className="verdict" style={{ marginBottom: "18px" }}>
              <div className="kicker">Attestation valide</div>
              <div className="score" style={{ fontSize: "40px" }}>
                {etat.niveauLibelle}
              </div>
              <div className="rank">{etat.points} points sur {MASTERY_MAX_POINTS}</div>
            </div>

            <table className="tab">
              <tbody>
                <tr>
                  <th>Numéro</th>
                  <td>{etat.serial}</td>
                </tr>
                <tr>
                  <th>Titulaire</th>
                  <td>{etat.nom ?? "Attestation sans nom"}</td>
                </tr>
                <tr>
                  <th>Métier déclaré</th>
                  <td>{etat.metier ?? "Non renseigné"}</td>
                </tr>
                <tr>
                  <th>Émise le</th>
                  <td>
                    {etat.emiseLe
                      ? new Date(etat.emiseLe).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="refer" style={{ marginTop: "18px" }}>
              <b>Ce que cette attestation prouve, et ce qu'elle ne prouve pas</b>
              {etat.portee}
            </div>
          </div>
        )}

        <p className="fine" style={{ marginTop: "24px" }}>
          <a href="/">Retour au diagnostic</a> · <a href="/methode">La méthode de calcul</a>
        </p>
      </div>
    </section>
  );
}
